import React from 'react';
import * as Yup from 'yup';
import { Button, Grid, Typography } from '@mui/material';
import { Form } from 'formik';
import { MuiForm } from 'lib/Form';
import { MuiModal } from 'ui/MuiModal';
import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { DatePickerFormField } from 'lib/form/fields/DatePickerFormField';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import { Row } from 'ui/Flex';
import { MESSAGE_CUSTOMER_EXPORT_CREATE_FAIL, MESSAGE_CUSTOMER_EXPORT_IN_PROGRESS } from 'config/messages';
import { useSnackbar } from 'notistack';
import { Option } from 'lib/types';
import { ICustomerExportOperation, INITIAL_OPERATION_RESULT } from 'components/customers/export/models';
import { customerExportsApi } from 'components/customers/export/customerExportsApi';
import { CustomerRole } from 'components/customers/models/Customer';
import {
    ExportFormValues,
    INITIAL_EXPORT_VALUES,
    getCustomersExportOptions
} from './hooks/useCustomersExportFilters';

interface ExportCustomersDialogProps {
    open: boolean;
    onCancel: () => void;
    onCustomerExportCreation?: (operation: ICustomerExportOperation) => void;
}

const ROLE_OPTIONS: Option[] = [
    { value: 'ALL', label: 'All' },
    { value: CustomerRole.USER, label: 'Registered' },
    { value: CustomerRole.GUEST, label: 'Guest' }
];

const MARKETING_OPTIONS: Option[] = [
    { value: 'ALL', label: 'All' },
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' }
];

const validationSchema = Yup.object().shape({
    updatedAfter: Yup.date()
        .nullable()
        .test(
            'after-created',
            'Updated after must be later than or equal to created after.',
            function (value) {
                const { createdAfter } = this.parent;
                if (!value || !createdAfter) {
                    return true;
                }
                return new Date(value) >= new Date(createdAfter);
            }
        )
});

export const ExportCustomersDialog: React.FC<ExportCustomersDialogProps> = ({
    open,
    onCancel,
    onCustomerExportCreation
}) => {
    const { enqueueSnackbar } = useSnackbar();

    const handleSubmit = React.useCallback(
        async (values: ExportFormValues) => {
            try {
                const options = getCustomersExportOptions(values);
                const response = await customerExportsApi.createExport(options);
                if (!response.ok) {
                    throw new Error(response.body.message);
                }
                if (onCustomerExportCreation) {
                    onCustomerExportCreation({ result: INITIAL_OPERATION_RESULT, ...response.body });
                }
                enqueueSnackbar(MESSAGE_CUSTOMER_EXPORT_IN_PROGRESS, { variant: 'info' });
                onCancel();
            } catch {
                enqueueSnackbar(MESSAGE_CUSTOMER_EXPORT_CREATE_FAIL, { variant: 'error' });
            }
        },
        [onCustomerExportCreation, onCancel, enqueueSnackbar]
    );

    return (
        <MuiModal open={open} onClose={onCancel}>
            <MuiForm
                initialValues={INITIAL_EXPORT_VALUES}
                onSubmit={handleSubmit}
                validationSchema={validationSchema}
                enableReinitialize
            >
                {({ submitForm, isSubmitting }) => (
                    <Form>
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <Typography variant="h6" color="primary">
                                    Export Customers
                                </Typography>
                            </Grid>
                            <Grid size={6}>
                                <SelectFormField
                                    name="role"
                                    label="Role"
                                    options={ROLE_OPTIONS}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid size={6}>
                                <SelectFormField
                                    name="hasAgreedToReceiveMarketing"
                                    label="Marketing consent"
                                    options={MARKETING_OPTIONS}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid size={6}>
                                <DatePickerFormField
                                    name="createdAfter"
                                    label="Created after"
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid size={6}>
                                <DatePickerFormField
                                    name="updatedAfter"
                                    label="Updated after"
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid size={12}>
                                <Row gutter align="flex-end">
                                    <Button color="primary" variant="outlined" onClick={onCancel}>
                                        Cancel
                                    </Button>
                                    <LoadingButton
                                        loading={isSubmitting}
                                        disabled={isSubmitting}
                                        variant="contained"
                                        color="primary"
                                        onClick={submitForm}
                                    >
                                        Submit
                                    </LoadingButton>
                                </Row>
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </MuiForm>
        </MuiModal>
    );
};
